import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import {
  listAdd,
  listDetailsGetById,
  listUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormSelect from '../../../components/form/FormSelect';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import FormDateInput from '../../../components/form/FormDateInput';
import { endOfDay, startOfDay, parse } from 'date-fns';
import FormToggle from '../../../components/form/FormToggle';
import { formatPayloadTime } from '../../../utils/common';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const AddEditListModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const timezone = useTimezone();

  const [processOptions, setProcessOptions] = useState([]);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    list_name: '',
    list_status: 'on',
    processid: '',
    start_time: startOfDay(today),
    end_time: endOfDay(today),
    multiple_dialing: 'on',
    time_zone: 'no'
  };

  const validationSchema = Yup.object({
    list_name: Yup.string().required('List name is required'),
    processid: Yup.string().required('Process is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const formattedStartDate = formatPayloadTime(values.start_time);
        const formattedEndDate = formatPayloadTime(values.end_time);
        const payload = { ...values, id: editRecord.list_id };
        payload['start_time'] = formattedStartDate;
        payload['end_time'] = formattedEndDate;
        if (values.time_zone === 'no') {
          delete payload['start_time'];
          delete payload['end_time'];
        }
        const response = await dispatch(listUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const formattedStartDate = formatPayloadTime(values.start_time);
        const formattedEndDate = formatPayloadTime(values.end_time);
        const payload = { ...values };
        payload['start_time'] = formattedStartDate;
        payload['end_time'] = formattedEndDate;
        if (values.time_zone === 'no') {
          delete payload['start_time'];
          delete payload['end_time'];
        }
        const response = await dispatch(listAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
  }, []);

  const parseTime = (timeString) => {
    if (timeString.includes(' ')) {
      const timeOnly = timeString.split(' ')[1];
      return parse(timeOnly, 'HH:mm:ss', today);
    } else {
      return parse(timeString, 'HH:mm', today);
    }
  };
  const fetchListRecord = async () => {
    try {
      const response = await dispatch(listDetailsGetById(editRecord?.list_id)).unwrap();
      if (response) {
        const startTime = response.start_time ? parseTime(response.start_time) : startOfDay(today);

        const endTime = response.end_time ? parseTime(response.end_time) : endOfDay(today);

        setValues({
          ...response,
          start_time: startTime,
          end_time: endTime
        });
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchListRecord();
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit List' : 'Add List'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormToggle
                trueName="Yes"
                falseName="No"
                name="time_zone"
                label="Time Based"
                value={String(values.time_zone === 'yes')}
                onChange={() => {
                  setFieldValue('time_zone', values.time_zone === 'yes' ? 'no' : 'yes');
                  if (values.time_zone === 'no') {
                    setFieldValue('start_time', startOfDay(today));
                    setFieldValue('end_time', endOfDay(today));
                  }
                }}
              />
            </Col>
            {values?.time_zone === 'yes' && (
              <Col lg="3">
                <FormDateInput
                  label="Start Date"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  showTimeSelect={true}
                  name="start_time"
                  timeIntervals="1"
                  value={values.start_time}
                  showTimeSelectOnly={true}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values?.time_zone === 'yes' && (
              <Col lg="3">
                <FormDateInput
                  label="End Date"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  showTimeSelect={true}
                  name="end_time"
                  timeIntervals="1"
                  value={values.end_time}
                  showTimeSelectOnly={true}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            <Col lg="3">
              <FormInput
                name="list_name"
                label="List Name"
                placeholder="Enter List Name"
                value={values.list_name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                trueName="On"
                falseName="Off"
                name="list_status"
                label="Status"
                value={String(values.list_status === 'on')}
                onChange={() =>
                  setFieldValue('list_status', values.list_status === 'on' ? 'off' : 'on')
                }
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="processid"
                label="Process"
                placeholder="Select Process"
                value={values.processid}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                trueName="On"
                falseName="Off"
                name="multiple_dialing"
                label="Multiple Dialing"
                value={String(values.multiple_dialing === 'on')}
                onChange={() =>
                  setFieldValue('multiple_dialing', values.multiple_dialing === 'on' ? 'off' : 'on')
                }
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditListModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditListModal;
